import React from 'react';
import QRCode from 'react-qr-code';

import { Button, Heading, Modal, Notification, Stack, Text } from '@auspost/postmaster-react';

import PasscodeLockIcon from 'assets/passcode-lock.svg';
import PasscodeLockInactiveIcon from 'assets/passcode-lock-inactive.svg';
import ScanQrIcon from 'assets/scan-qr-code.svg';
import ScanQrInactiveIcon from 'assets/scan-qr-code-inactive.svg';

import TabSwitcher, { TabOption } from '../../../../../common/TabSwitcher/TabSwitcher';

import styles from './AccessCodeCTA.module.scss';

export enum AccessCodeTabSwitcherOptions {
  SCAN = `scan`,
  ENTER_CODE = `enter-code`,
}

const AccessCodeCTA: React.FC<{
  accessCode: string;
  accessNumber?: string | null;
  buildingAccessInstruction?: string | null;
  mobileNumber?: string | null;
}> = ({ accessCode, accessNumber, buildingAccessInstruction, mobileNumber }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<AccessCodeTabSwitcherOptions | string>(
    AccessCodeTabSwitcherOptions.SCAN
  );

  const displayNotification = () => {
    if (!buildingAccessInstruction) return null;
    const accessDetailsNotificationContent = (
      <Stack space='xxsmall'>
        <Text variant='body-small' weight='bold'>
          Access details
        </Text>
        <Text variant='body-small'>{buildingAccessInstruction}</Text>
      </Stack>
    );
    return (
      <div className={styles.notification}>
        <Notification type='alert' status={'info'} message={accessDetailsNotificationContent} isDismissible={false} />
      </div>
    );
  };
  const handleModalClose = () => {
    setActiveTab(AccessCodeTabSwitcherOptions.SCAN);
    setIsModalOpen(false);
  };

  const displayAccessNumber = () => {
    const formattedAccessNumber = accessNumber
      ? `${accessNumber.slice(0, 3)} ${accessNumber.slice(3, 6)} ${accessNumber.slice(6)}`
      : '';

    return (
      <Text variant='body-small' className={styles.instruction}>
        <Text variant='body-small' as='span'>
          4
        </Text>
        {formattedAccessNumber ? (
          <>
            Enter this access number:
            <Text variant='body-large' as='span' className={`${styles.accessCode} ${styles.accessCodeNumber}`}>
              {formattedAccessNumber}
            </Text>
          </>
        ) : (
          <>Enter your mobile number.</>
        )}
      </Text>
    );
  };

  const getQRCodeValue = () => {
    // use access number when the account  has low trust mobile or no mobile number
    if (accessNumber) return `*9868${accessNumber}MYPOST:P:${accessCode}`;
    // otherwise, use mobileNumber
    return `*9868${mobileNumber}MYPOST:P:${accessCode}`;
  };
  const tabs: TabOption[] = [
    {
      value: AccessCodeTabSwitcherOptions.SCAN,
      label: 'Scan QR code',
      ariaControls: 'ScanQrCodeTabPanel',
      testId: 'scan-qr-code-tab',
      icon:
        activeTab === AccessCodeTabSwitcherOptions.SCAN ? (
          <ScanQrIcon aria-hidden />
        ) : (
          <ScanQrInactiveIcon aria-hidden />
        ),
      iconPosition: 'left',
    },
    {
      value: AccessCodeTabSwitcherOptions.ENTER_CODE,
      label: 'Enter code',
      ariaControls: 'EnterCodeTabPanel',
      testId: 'enter-code-tab',
      icon:
        activeTab === AccessCodeTabSwitcherOptions.ENTER_CODE ? (
          <PasscodeLockIcon aria-hidden />
        ) : (
          <PasscodeLockInactiveIcon aria-hidden />
        ),
      iconPosition: 'left',
    },
  ];

  return (
    <>
      <div className={styles.accessCodeCta}>
        <Button
          className={styles.accessCodeButton}
          onClick={() => setIsModalOpen(true)}
          variant='secondary'
          size='small'
        >
          <ScanQrIcon aria-hidden />
          Access Parcel Locker
        </Button>
      </div>
      <Modal
        isModalShowing={isModalOpen}
        onHide={handleModalClose}
        modalClassName={styles.accessCodeModal}
        dialogClassName={styles.accessCodeDialog}
        ariaDialogLabel='QR code modal'
        dialogId='QR code modal'
      >
        <Modal.Header closeButton={true} className={styles.modalHeader}>
          <Heading level='h3' isBold>
            Parcel Locker access
          </Heading>
        </Modal.Header>
        {displayNotification()}
        <Modal.Content className={styles.modalContent}>
          <TabSwitcher
            activeTab={activeTab}
            tabs={tabs}
            onChange={(e) => setActiveTab(e.target.value)}
            className={styles.tabSwitcher}
            activeLabelColor='#212129'
            labelColor='#6D6D72'
          />
          <div className={styles.content}>
            {activeTab === AccessCodeTabSwitcherOptions.ENTER_CODE ? (
              <div className={styles.enterCodeWrapper}>
                <Heading level='h6' isBold>
                  Enter code
                </Heading>
                <Text variant='body-small' className={styles.instruction}>
                  On the locker touchscreen:
                </Text>
                <Text variant='body-small' className={styles.instruction}>
                  <Text variant='body-small' as='span'>
                    1
                  </Text>
                  Select <b>collect.</b>
                </Text>
                <Text variant='body-small' className={styles.instruction}>
                  <Text variant='body-small' as='span'>
                    2
                  </Text>
                  Enter this access code:
                  <Text variant='body-large' as='span' className={styles.accessCode}>
                    {accessCode}
                  </Text>
                </Text>
                <Text variant='body-small' className={styles.instruction}>
                  <Text variant='body-small' as='span'>
                    3
                  </Text>
                  Select <b>next.</b>
                </Text>
                {displayAccessNumber()}
                <Text variant='body-small' className={styles.instruction}>
                  <Text variant='body-small' as='span'>
                    5
                  </Text>
                  Select <b>next</b> to open the locker door.
                </Text>
              </div>
            ) : (
              <div className={styles.qrCodeWrapper}>
                <Heading level='h6' isBold>
                  Scan QR code
                </Heading>
                <Text variant='body-small' className={styles.instruction}>
                  Use the scanner near the locker touchscreen. The locker door will open.
                </Text>
                <QRCode value={getQRCodeValue()} aria-label='Parcel Locker access' height={160} />
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer alignment='center' className={styles.modalFooter}>
          <Button onClick={handleModalClose} size='medium' variant='primary' className={styles.closeButton}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccessCodeCTA;
